import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { VyLoader } from '../../../../components/common/VyLoader';
import VySEO from '../../../../components/common/VySEO';
import PageBgOverlay from '../../../../components/event/pageBuilder/PageBgOverlay';
import PageSettingsSidebar from '../../../../components/event/pageBuilder/PageSettingsSidebar';
import PBUpdateButtons from '../../../../components/event/pageBuilder/PBUpdateButtons';
import VystemLight from '../../../../components/event/templates/VystemLight';
import usePagebuilder from '../../../../hooks/pagebuilder/usePagebuilder';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import { useDataLoader } from '../../../../hooks/useDataLoader';
import { useEventAccess } from '../../../../hooks/useEventAccess';
import { usePages } from '../../../../hooks/usePages';
import { usePermission } from '../../../../hooks/usePermission';
import { EventInterface } from '../../../../interfaces/interfaces';
import Layout from '../../../../layouts/BlackStripedHeader';
import EventPasswordLogin from '../../../../layouts/EventPasswordLogin';
import EventWrapper from '../../../../layouts/EventWrapper';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../services/helper';
import { getEventStaticPaths, getEventStaticProps } from '../../../../utils/eventStaticHelper';
import { isLoggedInToEvent } from '../../../../utils/auth';

function CustomPage({ initEvent, pagename }: { initEvent: EventInterface; pagename?: string }) {
  const user = useSelector((state) => state.common.user);

  const pageData = useSelector((state) => state.common.pagedata);
  const event = useSelector((state) => state.common.event);
  const pages = useSelector((state) => state.common.pages);
  const eventRegistration = useSelector((state) => state.common.eventRegistration);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const eventDataLoadedId = useSelector((state) => state.common.eventDataLoadedId);

  const { t } = useTranslation();
  const router = useRouter();
  const { isLoadingData, loadData } = useDataLoader();
  const { redirectSpecialPages, getUrlForPageId } = usePages();
  const { ct } = useCustomTranslations();
  const { isEventHost } = usePermission();
  const { checkEventDisabled } = useEventAccess();
  const { isUpdating, updatePageBuilder, cancelChanges } = usePagebuilder();

  const isMyEvent = initEvent?.organization._id === user?.organization?._id;

  useEffect(() => {
    if (initEvent && pagename && (!eventDataLoadedId || eventDataLoadedId !== event?._id)) loadData(initEvent, pagename);
  }, [initEvent, pagename, eventDataLoadedId]);

  useEffect(() => {
    if (pageData && event && !isLoadingData && pagename === pageData.name) redirectSpecialPages(pageData, event.slug);
  }, [pageData, event, pagename, isLoadingData]);

  useEffect(() => {
    if (!pageData) return;
    if (checkEventDisabled(event) && !isMyEvent && initEvent?.settings.redirectDisabledEvent && pageData?._id !== initEvent?.settings.eventAccessDisabledPage) {
      const url = getUrlForPageId(initEvent?.settings.eventAccessDisabledPage, pages, initEvent?.slug);
      if (url) {
        location.href = url;
      }
    }
  }, [initEvent, pages, pageData]);

  if (checkEventDisabled(event) && !isMyEvent && !initEvent?.settings.redirectDisabledEvent) {
    return (
      <div className="grid w-full h-screen bg-black place-items-center">
        <h1 className="text-2xl text-white">{t('event:event-is-in-the-past')}</h1>
      </div>
    );
  }

  if (router.isFallback || isLoadingData || !event) {
    return <VyLoader backgroundColor={initEvent?.template?.mainColor} loaderColor={initEvent?.template?.mainButtonTextColor} solidBackground={true} />;
  }

  const isEventMember = (eventRegistration?.event === event?._id && isLoggedInToEvent(event._id)) || isEventHost();
  if (!isEventMember && event?.settings.allowEventPasswordLogin) return <EventPasswordLogin event={event} />;

  return (
    <div className="bg-cover">
      <VySEO
        title={`${ct(pageData?.name, pageData) || pagename || ''} | ${initEvent?.name}`}
        description={initEvent?.description}
        image={{ url: createDisplayLink(initEvent?.template?.logo) }}
        vystemEvent={initEvent}
      />
      <EventWrapper>
        <Layout>
          {!isMobilePreview && (
            <div className="fixed w-screen h-screen -z-10">
              <PageBgOverlay />
            </div>
          )}
          {templateEditMode && <PageSettingsSidebar />}
          <PBUpdateButtons isUpdating={isUpdating} onUpdate={updatePageBuilder} onCancel={cancelChanges} />
          <VystemLight />
        </Layout>
      </EventWrapper>

      {/* maybe move to global.tsx */}
      {initEvent && (
        <style jsx global>{`
          .link {
            &:hover {
              color: var(--link-hover-color);
            }
          }

          .bg-buttons-main {
            background-color: ${initEvent?.template?.mainColor || 'var(--vystem-button-text)'};
            color: ${initEvent?.template?.mainButtonTextColor || 'var(--isdi-grey)'};
          }

          :root {
            --scheme-main: ${initEvent?.template?.mainColor || 'var(--vystem-blue)'};
            --scheme-secondary: ${initEvent?.template?.complementColor || 'var(--isdi-grey)'};
            --buttons-main: ${initEvent?.template?.mainButtonTextColor || 'var(--vystem-button-text)'};
            --buttons-secondary: ${initEvent?.template?.complementButtonTextColor || 'var(--vystem-button-text)'};
            --link-hover-color: ${initEvent?.template?.mainColor ?? 'var(--vystem-blue)'};
          }
        `}</style>
      )}
    </div>
  );
}

export const getStaticProps: GetServerSideProps = async (context) => {
  return getEventStaticProps(context);
};

export async function getStaticPaths() {
  return {
    paths: await getEventStaticPaths(true),
    fallback: true,
  };
}

export default CustomPage;
